import { useEffect, useRef } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { cn } from "src/lib/cn";
import { Input } from "src/sudokit/components/Input";
import { Text } from "src/sudokit/components/Text";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import IconButton from "../IconButton";
import InlineSVG from "react-inlinesvg";
import { motion } from "framer-motion";

type ChatNavigationProps = {
  view: "chat" | "list";
  chatTitle: string;
  onBack: () => void;
  onNewChat: () => void;
  onOpenSettings?: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearching: boolean;
  setIsSearching: (isSearching: boolean) => void;
};

export const ChatNavigation: React.FC<ChatNavigationProps> = ({ view, chatTitle, onBack, onNewChat, onOpenSettings, searchQuery, setSearchQuery, isSearching, setIsSearching }) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearching && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearching]);

  if (view === "chat") {
    return (
      <>
        <WithReset className="absolute top-0 left-0 right-0 z-50 h-[4rem] bg-app-document-background-no-opacity">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0 }}
            className="h-full flex items-center justify-between gap-2 -ml-1"
            key={"chat-mode"}
          >
            <IconButton
              tooltipDelay={500}
              src="/assets/standard-arrow-left.svg"
              aria-label="Back to chat list"
              hoverTipText="Your chats"
              size={32}
              iconSize={24}
              onClick={onBack}
              className="[&_path]:fill-app-grey-1"
            />
            <Text className={cn("flex-1 font-medium truncate text-left", "text-app-grey-0")}>{chatTitle}</Text>
            <IconButton
              tooltipDelay={500}
              src="/assets/add.svg"
              aria-label="New chat"
              hoverTipText="New chat"
              size={32}
              iconSize={24}
              onClick={() => {
                AppEventPublisher.agenticChat.newChat("button");
                onNewChat();
              }}
              className="[&_path]:fill-app-grey-1"
            />
            {onOpenSettings && (
              <IconButton
                tooltipDelay={500}
                src="/assets/gear-v2.svg"
                aria-label="Chat personalization"
                hoverTipText="Personalization"
                size={32}
                iconSize={24}
                onClick={onOpenSettings}
                className="[&_path]:fill-app-grey-1"
              />
            )}
          </motion.div>
        </WithReset>
      </>
    );
  }

  if (isSearching) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2 } }}
        exit={{ opacity: 0 }}
        className="flex items-center gap-2 py-2 h-14 pb-4 -ml-2"
        key={"search"}
      >
        <IconButton
          tooltipDelay={500}
          src="/assets/standard-arrow-left.svg"
          aria-label="Close search"
          hoverTipText="Close"
          size={32}
          iconSize={24}
          onClick={() => {
            setIsSearching(false);
            setSearchQuery("");
          }}
          className="[&_path]:fill-app-grey-1"
        />
        <div className="flex-1">
          <div className="relative">
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats..."
              mode="ghost"
              className="w-full pl-10"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsSearching(false);
                  setSearchQuery("");
                }
              }}
            />
            <InlineSVG
              src="/assets/search-v2.svg"
              className="absolute top-1/2 left-2 -translate-y-1/2 w-6 h-6 [&_path]:fill-app-grey-1 pointer-events-none"
            />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <WithReset>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2 } }}
        exit={{ opacity: 0 }}
        className="flex items-center justify-between gap-2 py-2 h-14 pb-4"
        key={"default"}
      >
        <Text className="font-bold text-app-grey-0">Your chats</Text>
        <div className="flex items-center gap-0.5">
          <IconButton
            src="/assets/search-v2.svg"
            aria-label="Search chats"
            hoverTipText="Search"
            tooltipDelay={500}
            size={32}
            iconSize={24}
            onClick={() => setIsSearching(true)}
            className="[&_path]:fill-app-grey-1"
          />
          <IconButton
            src="/assets/add.svg"
            aria-label="New chat"
            hoverTipText="New chat"
            size={32}
            tooltipDelay={500}
            iconSize={24}
            onClick={() => {
              AppEventPublisher.agenticChat.newChat("button");
              onNewChat();
            }}
            className="[&_path]:fill-app-grey-1"
          />
        </div>
      </motion.div>
    </WithReset>
  );
};
